import { lazy, Suspense, useState } from 'react'

import PencilAltIcon from '@heroicons/react/outline/esm/PencilAltIcon.js'
import { clsx } from 'clsx'

import { T } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'
import { TopBar } from '@tribeplatform/react-ui-kit/TopBar'

import { generateNewChatPath } from '../../../utils/messaging.utils.js'
import { MessagingDropdownLoading } from '../../Messaging/MessagingDropdownLoading.js'
import { NavbarControlItem } from '../constants.js'
import { MessagingIndicatorBadge } from '../MessagingIndicatorBadge.js'
import { getIcon } from '../utils.js'

export type NavbarControlMessagingProps = {
  item: NavbarControlItem
  className?: string
}

const MessagingDropdownList = lazy(() =>
  import('../../Messaging/MessagingDropdownList.js').then(m => ({
    default: m.MessagingDropdownList,
  })),
)

export const NavbarControlMessaging = ({
  item,
  className,
}: NavbarControlMessagingProps) => {
  const { Link } = useRouter()

  const [popoverActive, setPopoverActive] = useState(false)

  const close = () => setPopoverActive(false)

  const Icon = getIcon(item.icon)

  return (
    <Popover
      placement="bottom-end"
      open={popoverActive}
      onClose={close}
      activator={
        <MessagingIndicatorBadge className={clsx('translate-y-0')}>
          <TopBar.Button
            rounded
            onClick={() => setPopoverActive(!popoverActive)}
            icon={Icon ? <Icon /> : null}
            aria-label={item.label}
            className={className}
          />
        </MessagingIndicatorBadge>
      }
      fluidContent={false}
      fixed
    >
      <div className="flex flex-col">
        <div className="border-b border-line-subdued px-3 py-2 flex items-center gap-2">
          <Link
            className="flex-grow text-content-subdued font-bold hover:text-content-hovered rounded-base"
            href="/messaging"
            onClick={close}
          >
            <T defaultMessage="Messages" id="Generics.Messages" />
          </Link>

          <IconButton
            as={Link}
            href={generateNewChatPath()}
            icon={<PencilAltIcon />}
            variant="tertiaryNeutral"
            size="lg"
            className="shrink-0"
          />
        </div>
        <div className="grow">
          <Suspense fallback={<MessagingDropdownLoading />}>
            <MessagingDropdownList closeDropdown={close} />
          </Suspense>
        </div>
        <div className="border-t border-line-subdued flex align-middle items-center space-s-3">
          <Link
            className="flex-grow p-3 text-content-subdued text-center text-sm hover:text-content-hovered"
            href="/messaging"
            onClick={close}
          >
            <T defaultMessage="See all messages" id="Generics.SeeAllMessages" />
          </Link>
        </div>
      </div>
    </Popover>
  )
}
